import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchAdminDashboard,
  fetchDepartments,
  fetchProjects,
  fetchTaskTemplates,
} from './services/onboardingService';
import { GAMIFY_ADMIN_PREFIX, slugToPanel, tabToSlug, isPlayerView } from '../../utils/routeMap';
import Loader from '../../../components/loader';
import dashboardTabIcon from '../../../images/item.icon.webp';
import projectsTabIcon from '../../../images/adminImages/project.png';
import tasksTabIcon from '../../../images/adminImages/task.png';

// Lazy-load each tab so the initial admin bundle only contains the tab you
// land on. Bundle splits happen at the import boundary; Suspense fallback
// shows the same in-tab loader text the user already expects.
const Dashboard = lazy(() => import('./Dashboard'));
const BranchDetail = lazy(() => import('./BranchDetail'));
const Projects = lazy(() => import('./Projects'));
const Tasks = lazy(() => import('./Tasks'));
const Challenges = lazy(() => import('./Challenges'));
const SwagItems = lazy(() => import('./SwagItems'));
// Hidden — re-enable when admin Employee↔Department management is needed.
// const EmployeeDepartments = lazy(() => import('./EmployeeDepartments'));
const EmployeeOnboarding = lazy(() => import('./employee'));

const TABS = [
  {
    key: 'Dashboard',
    icon: <img src={dashboardTabIcon} alt="Dashboard" className="w-4 h-4 object-contain" />,
  },
  {
    key: 'Projects',
    icon: <img src={projectsTabIcon} alt="Projects" className="w-4 h-4 object-contain" />,
  },
  {
    key: 'Tasks',
    icon: <img src={tasksTabIcon} alt="Tasks" className="w-4 h-4 object-contain" />,
  },
  {
    key: 'Challenges',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 4h14M6 4v6a6 6 0 0012 0V4M12 16v4M8 20h8" />
      </svg>
    ),
  },
  {
    key: 'Swag',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
    ),
  },
  // Hidden — re-enable when admin Employee↔Department management is needed.
  // {
  //   key: 'Members',
  //   icon: (
  //     <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
  //       <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 014-4h0m0-4a4 4 0 110-8 4 4 0 010 8zm8 0a3 3 0 100-6 3 3 0 000 6z" />
  //     </svg>
  //   ),
  // },
];

// Shown while a lazy-loaded tab's JS chunk is being downloaded. Uses the same
// full-screen blur Loader as OnboardingProfiles / MyLinks so chunk-load and
// data-fetch present as one continuous loader instead of two flashes.
function TabFallback() {
  return <Loader />;
}

// Tab pills are portaled into the admin Header's slot (#admin-header-slot)
function HeaderTabs({ activeTab, onChange }) {
  const [slot, setSlot] = useState(null);

  useEffect(() => {
    setSlot(document.getElementById('admin-header-slot'));
  }, []);

  if (!slot) return null;

  return ReactDOM.createPortal(
    <div className="flex items-center gap-1 bg-white/70 backdrop-blur p-1 rounded-lg border border-slate-200 shadow-sm whitespace-nowrap">
      {TABS.map((t) => {
        const active = activeTab === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
              active
                ? 'bg-[#fb6635] text-white shadow-sm'
                : 'text-[#211b4f] hover:bg-orange-50'
            }`}
          >
            {t.icon}
            <span className="hidden sm:inline">{t.key}</span>
          </button>
        );
      })}
    </div>,
    slot
  );
}

function OnboardingSection() {
  const role = useSelector((state) => state.admin?.role);
  const token = useSelector((state) => state.admin?.token);
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedBranch, setSelectedBranch] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);

  // Derive activeTab early so the fetch effect can key off it.
  const slug = location.pathname.split('/').filter(Boolean)[1] || 'dashboard';
  const panel = slugToPanel(slug);
  const activeTab =
    panel?.tab && ['Dashboard', 'Projects', 'Tasks', 'Challenges', 'Swag'].includes(panel.tab)
      ? panel.tab
      : 'Dashboard';

  // Bumping reloadKey re-runs the per-tab effect (used by retry + post-mutation refresh).
  const [reloadKey, setReloadKey] = useState(0);
  const bumpReload = useCallback(() => setReloadKey((k) => k + 1), []);

  const reloadProjects = useCallback(() => {
    if (!token) return Promise.resolve();
    setProjectsLoading(true);
    setProjectsError(null);
    return Promise.all([
      fetchProjects({ token }),
      fetchDepartments({ token }),
      fetchTaskTemplates({ token }),
    ])
      .then(([p, d, t]) => {
        setProjects(Array.isArray(p) ? p : []);
        setDepartments(Array.isArray(d) ? d : []);
        setTemplates(Array.isArray(t) ? t : []);
        setProjectsLoading(false);
      })
      .catch((err) => setProjectsError(err || new Error('Failed to load projects')));
  }, [token]);

  const reloadTemplates = useCallback(() => {
    if (!token) return Promise.resolve();
    setTemplatesLoading(true);
    setTemplatesError(null);
    return Promise.all([
      fetchTaskTemplates({ token }),
      fetchDepartments({ token }),
    ])
      .then(([t, d]) => {
        setTemplates(Array.isArray(t) ? t : []);
        setDepartments(Array.isArray(d) ? d : []);
        setTemplatesLoading(false);
      })
      .catch((err) => setTemplatesError(err || new Error('Failed to load templates')));
  }, [token]);

  // Per-tab lazy fetch. Only the data needed for the active tab is loaded.
  // Loaders intentionally stay on if the API fails (no 200) — error state is
  // surfaced to the child so the user sees a retry, never a misleading empty UI.
  //
  // Critical: wait for BOTH role AND token before firing. On login they arrive
  // as two separate Redux updates (token first, role second). Without the
  // !role guard, the effect would fire once on token arrival (role still null)
  // and again on role arrival — duplicate API calls in the Network tab.
  useEffect(() => {
    if (!role || !token) return;
    if (isPlayerView(role)) return; // player view (branch managers + dept users) fetches its own data
    let cancelled = false;

    if (activeTab === 'Dashboard') {
      setDashboardLoading(true);
      setDashboardError(null);
      fetchAdminDashboard({ token })
        .then((data) => {
          if (cancelled) return;
          setDashboard(data);
          setDashboardLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setDashboardError(err || new Error('Failed to load dashboard'));
        });
    } else if (activeTab === 'Projects') {
      setProjectsLoading(true);
      setProjectsError(null);
      Promise.all([
        fetchProjects({ token }),
        fetchDepartments({ token }),
        fetchTaskTemplates({ token }),
      ])
        .then(([p, d, t]) => {
          if (cancelled) return;
          setProjects(Array.isArray(p) ? p : []);
          setDepartments(Array.isArray(d) ? d : []);
          setTemplates(Array.isArray(t) ? t : []);
          setProjectsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setProjectsError(err || new Error('Failed to load projects'));
        });
    } else if (activeTab === 'Tasks') {
      setTemplatesLoading(true);
      setTemplatesError(null);
      Promise.all([
        fetchTaskTemplates({ token }),
        fetchDepartments({ token }),
      ])
        .then(([t, d]) => {
          if (cancelled) return;
          setTemplates(Array.isArray(t) ? t : []);
          setDepartments(Array.isArray(d) ? d : []);
          setTemplatesLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setTemplatesError(err || new Error('Failed to load tasks'));
        });
    }

    return () => {
      cancelled = true;
    };
  }, [activeTab, role, token, reloadKey]);

  // Branch managers and department users (IT / Finance / Maketing / Training /
  // Branch Onboarding / Human Resources) all get the player gamified view.
  // EmployeeOnboarding mounts its own tab bar + URL-driven routing.
  if (isPlayerView(role)) {
    return (
      <Suspense fallback={<TabFallback />}>
        <EmployeeOnboarding />
      </Suspense>
    );
  }

  const handleTabChange = (tab) => {
    setSelectedBranch(null);
    navigate(`${GAMIFY_ADMIN_PREFIX}/${tabToSlug(tab)}`);
  };

  const renderTab = () => {
    if (activeTab === 'Dashboard') {
      if (selectedBranch) {
        return (
          <BranchDetail
            branch={selectedBranch}
            token={token}
            onBack={() => setSelectedBranch(null)}
          />
        );
      }
      return (
        <Dashboard
          dashboard={dashboard}
          loading={dashboardLoading}
          error={dashboardError}
          onRetry={bumpReload}
          onBranchClick={(b) => setSelectedBranch(b)}
          token={token}
        />
      );
    }
    if (activeTab === 'Projects') {
      return (
        <Projects
          projects={projects}
          token={token}
          departments={departments}
          templates={templates}
          loading={projectsLoading}
          error={projectsError}
          onRetry={bumpReload}
          onReload={reloadProjects}
        />
      );
    }
    if (activeTab === 'Tasks') {
      return (
        <Tasks
          templates={templates}
          token={token}
          departments={departments}
          loading={templatesLoading}
          error={templatesError}
          onRetry={bumpReload}
          onReload={reloadTemplates}
        />
      );
    }
    if (activeTab === 'Challenges') {
      return <Challenges token={token} />;
    }
    if (activeTab === 'Swag') {
      return <SwagItems token={token} />;
    }
    // Hidden — re-enable when admin Employee↔Department management is needed.
    // if (activeTab === 'Members') {
    //   return <EmployeeDepartments token={token} />;
    // }
    return null;
  };

  // Full-screen blur Loader matches the OnboardingProfiles experience: backend
  // content stays visible (blurred) underneath, loader vanishes when the active
  // tab's data arrives. On error we drop the loader so the inline retry UI
  // becomes interactable.
  const tabIsLoading =
    (activeTab === 'Dashboard' && dashboardLoading && !dashboardError) ||
    (activeTab === 'Projects' && projectsLoading && !projectsError) ||
    (activeTab === 'Tasks' && templatesLoading && !templatesError);
  // Swag and Challenges manage their own loading states internally.

  return (
    <div className="w-full flex flex-col">
      {tabIsLoading && <Loader />}
      <HeaderTabs activeTab={activeTab} onChange={handleTabChange} />

      <div className="p-3 sm:p-6 w-full">
        <Suspense fallback={<TabFallback />}>{renderTab()}</Suspense>
      </div>

    </div>
  );
}

export default OnboardingSection;
